import {
	MagnifyingGlassIcon,
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from "@/icons/solid";
import { get, orderBy } from "lodash";
import React, { useEffect } from "react";
import { useMemo } from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { getPayFrequencyName, roundTo2 } from "@/components/common/validation/Validators";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

const PayrollReviewReport = ({ columns, employees, checklist, withIssues, onCheck, forReport }) => {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(20);
	const start = page * itemsPerPage;
	const end = (page + 1) * itemsPerPage;

	const filteredEmployeeList = useMemo(() => {
		if (withIssues) {
			return employees?.filter(({ payslipId }) => withIssues.includes(payslipId));
		}

		const filterByValue = (o) =>
			["fullName"].some((k) => String(o[k])?.toLowerCase().includes(search.toLowerCase()));

		const filterResult = search ? employees?.filter(filterByValue) : employees;
		const sortedResult = orderBy(filterResult, ["ready", "fullName"], ["desc", "asc"]);

		return sortedResult;
	}, [search, JSON.stringify(employees)]);

	const lastPage = Math.ceil(filteredEmployeeList?.length / itemsPerPage);

	return (
		<div>
			<h3 className="font-w700 text-left">Payroll Report</h3>
			{!withIssues && (
				<div
					style={{
						border: "1px solid #bbbcbc",
						borderBottom: "none",
						position: "relative",
					}}
				>
					<MagnifyingGlassIcon
						className={"text-gray"}
						style={{
							height: "1.5em",
							position: "absolute",
							margin: "0.75em",
						}}
					/>
					<input
						name="search"
						onChange={(e) => setSearch(e.target.value)}
						value={search}
						style={{
							border: "none",
							display: "inline-block",
							maxWidth: "100%",
							width: "100%",
							padding: 10,
							paddingLeft: 40,
						}}
					/>
				</div>
			)}
			<table className="table table-responsive table-hover table-striped">
				<thead>
					<tr>
						<th width="5%"></th>
						{columns.map(([header], index) => {
							return <th key={index}>{header}</th>;
						})}
					</tr>
				</thead>
				<tbody>
					{filteredEmployeeList?.length > 0 ? (
						filteredEmployeeList?.slice(start, end)?.map((employee) => {
							const rows = columns.map(([header, key]) => key);
							return (
								<EmployeeRow
									key={employee.id}
									forReport={forReport}
									onCheck={onCheck}
									checklist={checklist}
									employee={employee}
									rows={rows}
								/>
							);
						})
					) : (
						<tr>
							<th colSpan={6}>No Employees {search ? `matching ${search}` : "this payroll"}</th>
						</tr>
					)}
				</tbody>
				<Pagination {...{ page, setPage, lastPage, list: filteredEmployeeList }} />
			</table>
		</div>
	);
};

const formatter = (value, format) => {
	switch (format) {
		case "decimal":
			return roundTo2(parseFloat(value));
		default:
			return value;
	}
};

const EmployeeRow = ({ employee, rows, onCheck, checklist, forReport }) => {
	const checked = checklist?.includes(employee.payslipId);

	return (
		<tr style={{ textAlign: "left" }}>
			<td width="5%">
				{forReport && (
					<label className="css-input css-checkbox css-checkbox-primary remove-margin-t remove-margin-b">
						<input
							type="checkbox"
							checked={checked}
							onChange={() => {
								onCheck && onCheck(employee.payslipId);
							}}
						/>
						<span />
					</label>
				)}
			</td>
			{rows?.map((key, index) => {
				let path = "";
				let prefix = "";
				let postfix = "";
				let type = "";

				let valueToDisplay = "";

				switch (typeof key) {
					case "object":
						{
							path = key.path;
							prefix = key.prefix;
							type = key.type;
						}
						break;
					case "string":
						{
							path = key;
						}
						break;
				}

				if (path === "@latestSalary") {
					const salaries = employee?.salaries;
					const { salaryAmount, payPeriodFrequency } = salaries?.[salaries?.length - 1] || {};

					valueToDisplay = salaryAmount;
					postfix = `/${getPayFrequencyName({ payFrequency: payPeriodFrequency })}`;
				} else {
					valueToDisplay = get(employee, path);
				}

				return (
					<td key={index}>
						{prefix}
						{formatter(valueToDisplay, type)}
						{postfix}
						{!employee.ready && path === "fullName" && (
							<ExclamationTriangleIcon
								className="h-7 w-7 text-danger"
								style={{ verticalAlign: "sub" }}
							/>
						)}
					</td>
				);
			})}
		</tr>
	);
};

const Pagination = ({ page, setPage, lastPage, list }) => {
	if (list?.length <= 0 || lastPage <= 1) return null;
	return (
		<thead>
			<tr>
				<th colSpan={7}>
					<div
						style={{
							display: "grid",
							gridGap: "1.5rem",
							grid: "1.5rem",
							gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
						}}
					>
						<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
							<button
								className="btn btn-xs btn-default pull-left"
								title="Go To Page 1"
								style={{ marginLeft: "0.5rem" }}
								onClick={() => {
									if (page != 0) setPage(0);
								}}
								disabled={page === 0}
							>
								<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
							</button>
							<button
								className="btn btn-xs btn-default pull-left"
								title="Go Back 5 Pages"
								style={{ marginLeft: "0.5rem" }}
								onClick={() => {
									if (page != 0) setPage(Math.max(page - 5, 0));
								}}
								disabled={page === 0}
							>
								<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
							</button>
							<button
								className="btn btn-xs btn-default pull-left"
								title="Go Back 1 Page"
								style={{ marginLeft: "0.5rem" }}
								onClick={() => {
									if (page != 0) setPage(page - 1);
								}}
								disabled={page === 0}
							>
								<ChevronLeftIcon style={{ height: "1.5em", float: "left" }} />
							</button>
						</div>
						<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
							<strong>
								Page {page + 1}/{lastPage}
							</strong>
						</div>
						<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
							<button
								className="btn btn-xs btn-default pull-right"
								style={{ marginRight: "0.5rem" }}
								title={`Go to Page ${lastPage}`}
								onClick={() => {
									if (page !== lastPage - 1) setPage(lastPage - 1);
								}}
								disabled={page === lastPage - 1}
							>
								<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
							</button>
							<button
								className="btn btn-xs btn-default pull-right"
								style={{ marginRight: "0.5rem" }}
								title="Go Forward 5 Pages"
								onClick={() => {
									if (page !== lastPage - 1) setPage(Math.min(page + 5, lastPage - 1));
								}}
								disabled={page === lastPage - 1}
							>
								<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
							</button>
							<button
								className="btn btn-xs btn-default pull-right"
								title="Go Forward 1 Page"
								onClick={() => {
									if (page !== lastPage - 1) setPage(page + 1);
								}}
								style={{ marginRight: "0.5rem" }}
								disabled={page === lastPage - 1}
							>
								<ChevronRightIcon style={{ height: "1.5em", float: "left" }} />
							</button>
						</div>
					</div>
				</th>
			</tr>
		</thead>
	);
};

const mapStateToProps = (state) => {
	const currency = state.company?.settings?.currency;

	const columns = {
		MT: [
			["Employee", "fullName"],
			[
				"Salary",
				{
					path: "@latestSalary",
					type: "decimal",
					prefix: currency,
				},
			],
			[
				"Gross",
				{
					path: "payslip[gross]",
					type: "decimal",
					prefix: currency,
				},
			],
			[
				"Net",
				{
					path: "payslip[net]",
					type: "decimal",
					prefix: currency,
				},
			],
		],
		UK: [
			["Employee", "fullName"],
			[
				"Salary",
				{
					path: "@latestSalary",
					type: "decimal",
					prefix: currency,
				},
			],
			[
				"Gross",
				{
					path: "payslip[gross]",
					type: "decimal",
					prefix: currency,
				},
			],
			[
				"Net",
				{
					path: "payslip[net]",
					type: "decimal",
					prefix: currency,
				},
			],
		],
	};

	const employees = (state.payroll?.employees || []).map((employee) => {
		employee.ready = ["yes", "true", true].includes(employee.payslip?.ready);
		return employee;
	});

	return {
		columns: columns[state.company?.settings?.jurisdiction],
		employees,
	};
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PayrollReviewReport);
